/**
 * Oto-Kupon Kodu Uygulayıcı (Auto-Code Applier)
 *
 * Çalışma: Sepet her güncellendiğinde backend'den aktif kupon listesini alır.
 * Tanımlı scope (collection/product) ile sepet içeriği eşleşiyorsa ve kupon
 * henüz uygulanmamışsa `/discount/CODE` fetch eder → Shopify session'a kodu
 * kaydeder → sonraki cart render'ında indirim satırı otomatik görünür.
 *
 * Shopify combinesWith: productDiscounts + orderDiscounts → başka automatic
 * discount varsa ikisi birden uygulanır (stack).
 *
 * Güvenlik: fetch /discount/CODE public bir endpoint, rate-limit için
 * sessionStorage flag ile aynı kodu tekrar tekrar deneme yapmayız.
 */
(function () {
  'use strict';

  const API_URL = 'https://dolcevita-massimo.ngrok.io/api/auto-codes/public/active';
  const CAMPAIGN_API_URL = 'https://dolcevita-massimo.ngrok.io/api/campaigns/public/active';
  let _campaigns = null;
  let _campaignsExpires = 0;
  const STORAGE_KEY = 'auto_code_applied_v2'; // bumped — old flag stale, ignore
  const DEBOUNCE_MS = 600;
  const DEBUG = true; // production'da false yap

  let _rules = null; // backend response cached in memory
  let _checking = false;
  let _lastCartVersion = null;
  let _debouncedTimer = null;

  function log() { if (DEBUG) console.log.apply(console, ['[auto-code]'].concat([].slice.call(arguments))); }
  function warn() { if (DEBUG) console.warn.apply(console, ['[auto-code]'].concat([].slice.call(arguments))); }

  log('script loaded v8 — cart/update.js apply + fallback');

  // Pembe kart sayacı bittiğinde tetiklenir → sepetten kuponu kaldır
  // (Müşteri sayfada açık bekliyor + tam o anda kupon süresi dolduysa)
  window.addEventListener('pcb:coupon-expired', function () {
    log('⏰ pcb:coupon-expired event — cache invalide + sepet recheck');
    _rules = null;          // fetchRules cache temizle
    _campaigns = null;       // campaigns cache de temizle
    _campaignsExpires = 0;
    // 500ms sonra recheck — Shopify discount sync'i için zaman ver
    setTimeout(function () {
      if (typeof checkAndApply === 'function') checkAndApply();
    }, 500);
  });

  // Shopify cart'ta belirli bir indirim kodunun zaten uygulanmış olup olmadığını
  // kontrol eder. cart.cart_level_discount_applications ve her item'ın
  // line_level_discount_allocations'ı taranır. Daha sağlam — sessionStorage
  // bayrağına güvenmez (yeni tab/incognito senaryosunda yenilenir).
  function isCodeAppliedToCart(code, cart) {
    if (!cart) return false;
    const cl = cart.cart_level_discount_applications || [];
    for (const d of cl) {
      if (d && (d.title === code || d.code === code)) return true;
    }
    const items = cart.items || [];
    for (const item of items) {
      const allocs = item.line_level_discount_allocations || [];
      for (const a of allocs) {
        const da = a && a.discount_application;
        if (da && (da.title === code || da.code === code)) return true;
      }
    }
    return false;
  }

  // Replit "discount-titles" çevirici tema'da çalışıyor ve bizim kodumuzun
  // başlığını eski mapping'le ezi yor. Admin'de tanımladığımız title'ı zorla
  // tekrar uygulamak için sepet DOM'unu izleyip override ediyoruz.
  // Map: code → admin title (fetchRules sonrası dolar)
  const _codeTitleMap = new Map();

  function syncTitlesFromRules(rules) {
    _codeTitleMap.clear();
    (rules.codes || []).forEach(c => { if (c.code && c.title) _codeTitleMap.set(c.code, c.title); });
  }

  function overrideDiscountTitles() {
    if (!_codeTitleMap.size) return;
    document.querySelectorAll('.dvb-discount-title[data-code]').forEach(el => {
      const code = el.getAttribute('data-code');
      const wantTitle = _codeTitleMap.get(code);
      if (wantTitle && el.textContent.trim() !== wantTitle) {
        el.textContent = wantTitle;
      }
    });
  }

  // MutationObserver — sepet drawer veya cart-page yenilendiğinde tetiklen
  function setupTitleOverrideObserver() {
    const obs = new MutationObserver(() => overrideDiscountTitles());
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    log('title-override observer active');
  }
  setupTitleOverrideObserver();

  // Session boyunca uygulanan kodları takip et — tekrar uygulamayı engelle
  function getApplied() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function markApplied(code) {
    const arr = getApplied();
    if (!arr.includes(code)) arr.push(code);
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch (_) {}
  }

  async function fetchRules() {
    if (_rules) return _rules;
    try {
      const r = await fetch(API_URL, { credentials: 'omit', mode: 'cors' });
      if (!r.ok) return { codes: [] };
      const j = await r.json();
      _rules = j && j.ok ? j : { codes: [] };
      syncTitlesFromRules(_rules);
      return _rules;
    } catch (err) {
      warn('fetchRules fail:', err);
      return { codes: [] };
    }
  }

  /**
   * Aktif kampanyaları çek — her 60 saniyede bir cache'den, yoksa fresh.
   * Kampanya bağlı kuponları storefront'ta kontrol etmek için kullanılır.
   * Kampanya aktif DEĞİLSE bağlı kupon UYGULANMAZ (PULL — DUAL senkron).
   */
  async function fetchCampaigns() {
    const now = Date.now();
    if (_campaigns !== null && _campaignsExpires > now) return _campaigns;
    try {
      const r = await fetch(CAMPAIGN_API_URL, { credentials: 'omit', mode: 'cors' });
      if (!r.ok) { _campaigns = []; _campaignsExpires = now + 30000; return _campaigns; }
      const j = await r.json();
      _campaigns = (j && j.ok && Array.isArray(j.campaigns)) ? j.campaigns : [];
      _campaignsExpires = now + 60000; // 60 sn cache
      return _campaigns;
    } catch (err) {
      warn('fetchCampaigns fail (graceful):', err);
      _campaigns = [];
      _campaignsExpires = now + 30000;
      return _campaigns;
    }
  }

  /**
   * Kupon kampanyaya bağlıysa, kampanyanın AKTİF olduğunu doğrula.
   * Bağlı değilse veya kampanya aktifse → true (uygulanabilir).
   * Bağlı ama kampanya aktif değilse → false (uygulama).
   */
  async function isCampaignAllowed(rule) {
    if (!rule.campaignId) return true; // kampanyaya bağlı değil → her zaman uygulan
    const camps = await fetchCampaigns();
    const camp = camps.find(c => c.id === rule.campaignId);
    if (!camp) return false; // kampanya bulunamadı (silindi/paused) → uygulama
    return camp.status === 'active'; // sadece aktif kampanyada uygula
  }

  async function getCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'include' });
      return await r.json();
    } catch (_) { return null; }
  }

  // Kural kapsamı cart ile eşleşiyor mu?
  function matchesScope(rule, cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return false;

    // Min subtotal kontrolü (TL)
    if (rule.minSubtotal && cart.items_subtotal_price < rule.minSubtotal * 100) return false;

    const scope = rule.scope || {};
    if (scope.type === 'all') return true;

    if (scope.type === 'product') {
      const handles = new Set((scope.productHandles || []));
      return cart.items.some(it => handles.has(it.handle));
    }

    if (scope.type === 'collection') {
      // cart.js'te line_item.handle var ama collection yok. Ürünün hangi koleksiyonda
      // olduğunu storefront'ta bilmek için: window.Shopify'da tag/type gelmez.
      // Çözüm: sayfa tipi collection ise Shopify'ın o page context'ini kullan.
      //   - /collections/kids-serisi sayfasındaysak → match
      //   - değilsek: her ürünün `product` objesinde collections yok → matching yapamayız
      // Basit heuristic: cart'ta bir ürünün productType veya tags'i koleksiyon handle'ı
      // ile eşleşiyorsa match say. Ya da storefront'tan bakılamaz çünkü cart.items.handle
      // sadece ürün handle'ı. Doğru çözüm: backend'de collection → product handle
      // listesi expand et (public endpoint). Şu an scope.productHandles'a göre çalışıyor
      // product-level eşleşme için. Collection-level için ek bir check eklenebilir.

      // Fallback: varsa productHandles (collection expansion sonrası) onu kullan
      const handles = new Set((scope.productHandles || []));
      if (handles.size) return cart.items.some(it => handles.has(it.handle));
      return false;
    }

    return false;
  }

  // Eski/silinmiş kodlar session'da takılı kalıp yenisini engelliyor.
  // Cart'taki tüm discount_codes temizlemek için /cart/update.js'e {discount:''}
  // gönderiyoruz. Birkaç kez tekrarlamak gerekebilir.
  async function clearStaleCodes() {
    try {
      for (let i = 0; i < 4; i++) {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' })
        }).catch(() => {});
      }
      log('cleared stale codes (loop x4)');
    } catch (_) {}
  }

  async function applyCode(code) {
    try {
      // 1) Önce session'daki yığılmış eski kodları temizle (X4 loop)
      await clearStaleCodes();

      // 2) /cart/update.js ile YENİ kodu uygula — /discount/CODE redirect'e
      //    göre daha sağlam, response cart object'i olarak döner
      let appliedSuccess = false;
      try {
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: code })
        });
        const cart = await r.json();
        const found = (cart.discount_codes || []).find(c => c.code === code);
        if (found && found.applicable) {
          appliedSuccess = true;
          log('applied via /cart/update.js:', code);
        } else {
          warn('not applicable after update.js:', code, found);
        }
      } catch (e) { warn('update.js apply fail', e); }

      // 3) Fallback: /discount/CODE (ek path, tema-yıllık uyum)
      if (!appliedSuccess) {
        const url = '/discount/' + encodeURIComponent(code);
        await fetch(url, { credentials: 'include', redirect: 'manual' }).catch(() => {});
        log('fallback /discount/ tried:', code);
      }

      markApplied(code);

      // Cart'ı zorla yenile — empty cart/update.js Yay tema UI'ı re-render eder
      try {
        const upd = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { _autoCodeApplied: code } })
        });
        log('cart/update.js status:', upd.status);
      } catch (e) { warn('cart/update fail', e); }

      // Cart drawer HTML'ini Section Rendering API ile fresh çek + DOM'a yaz
      try {
        const sec = await fetch('/?sections=cart-drawer', { credentials: 'include' });
        if (sec.ok) {
          const j = await sec.json();
          const html = j && j['cart-drawer'];
          if (html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const sels = ['cart-drawer', '.hdt-mini-cart', '[data-cart-drawer]', '#shopify-section-cart-drawer'];
            for (const s of sels) {
              const newEl = doc.querySelector(s);
              const oldEl = document.querySelector(s);
              if (newEl && oldEl) {
                oldEl.innerHTML = newEl.innerHTML;
                log('drawer DOM refreshed via section render:', s);
                break;
              }
            }
          }
        }
      } catch (e) { warn('section render fail', e); }

      // Yay tema custom event'leri tetikle
      document.dispatchEvent(new CustomEvent('hdt:cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (window.hdt && typeof window.hdt.refreshCart === 'function') {
        try { window.hdt.refreshCart(); } catch (_) {}
      }
      setTimeout(overrideDiscountTitles, 600);
      setTimeout(overrideDiscountTitles, 1500);
    } catch (err) {
      warn('apply fail', code, err);
    }
  }

  async function checkAndApply() {
    if (_checking) return;
    _checking = true;
    try {
      const [rulesRes, cart] = await Promise.all([fetchRules(), getCart()]);
      // Title override her tick'te çalışsın — Replit script'i ezse de geri al
      overrideDiscountTitles();
      if (!cart || !cart.items || !cart.items.length) return;
      if (_lastCartVersion === cart.token + ':' + cart.item_count) return; // değişmedi
      _lastCartVersion = cart.token + ':' + cart.item_count;

      const rules = (rulesRes.codes || []);
      for (const rule of rules) {
        // Sepete zaten uygulanmış mı? (Shopify state üzerinden — sessionStorage'tan değil)
        if (isCodeAppliedToCart(rule.code, cart)) {
          log('already applied to cart:', rule.code);
          continue;
        }
        if (matchesScope(rule, cart)) {
          // Kampanya kontrolü — bağlı ama aktif değilse uygulama (PULL DUAL senkron)
          const campOk = await isCampaignAllowed(rule);
          if (!campOk) {
            log('skip — campaign not active for code:', rule.code, 'campaignId:', rule.campaignId);
            continue;
          }
          log('applying code:', rule.code, 'matches cart');
          await applyCode(rule.code);
        }
      }
      // Apply sonrası title override tekrar
      setTimeout(overrideDiscountTitles, 800);
      setTimeout(overrideDiscountTitles, 1800);
    } finally {
      _checking = false;
    }
  }

  function debouncedCheck() {
    clearTimeout(_debouncedTimer);
    _debouncedTimer = setTimeout(checkAndApply, DEBOUNCE_MS);
  }

  // ═══ HOOK POINTS ═══

  // Sayfa yüklenince ilk kontrol
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', debouncedCheck);
  } else {
    debouncedCheck();
  }

  // Shopify cart AJAX hook — /cart/add.js, /cart/update.js, /cart/change.js sonrası
  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      const args = arguments;
      const result = origFetch.apply(this, args);
      try {
        const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
        if (/\/cart\/(add|update|change|clear)\.js/.test(url)) {
          result.then(() => debouncedCheck()).catch(() => {});
        }
      } catch (_) {}
      return result;
    };
  }

  // Yay tema custom eventi varsa
  document.addEventListener('hdt:cart:updated', debouncedCheck);
  document.addEventListener('cart:refresh', debouncedCheck);
})();
